import React, { Fragment, useState, useEffect } from "react";
import { Row, Col, Pagination, Input, Button, DatePicker, Select } from "antd";
import { timeFormatOnly, changeMonthFormat, changeYearFormat, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE } from "helpers/commonFunctions";
import { exportCsvToXlsx } from "helpers/exportXlsx";
import { usePostDownloadCsv } from "hooks/csv/csv";
import { useEmployeeList } from "hooks/reports/reports";
import { useFirstInLastOutList } from "hooks/reports/reports"
import { useDepartmentList } from "hooks/configuration/employee/employee";
import moment from "moment";
import Image from "helpers/Image";
import "../style.css";
import TableStateRow from "components/tableState";

const { Option } = Select;

const ALL_OPTION = "all";

/** Card ID, Employee Name and Contractor -- frozen while the dates scroll. */
const FIXED_COLUMN_COUNT = 3;

const FirstInLastOut = () => {

  const [page, setPage] = useState(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [employeeList, setEmployeeList] = useState([]);
  const [employeeId, setEmployeeId] = useState(null);
  const [siteId] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const [yearFormat, setYearFormat] = useState(null);
  const [monthFormat, setMonthFormat] = useState(null);
  const [dates, setDates] = useState([]);
  const { data: employeeData } = useEmployeeList();
  const { data: departmentData } = useDepartmentList();
  const [initialValue, setInitialValue] = useState({
    year: yearFormat,
    month: monthFormat,
    departmentId: departmentId,
    employeeId: employeeId,
    searchKey: searchKey,
    sortField: "",
    sortMethod: "desc",
    pageNumber: pageNumber,
    pageSize: page,
  });
  const [initialValuesSet, setInitialValuesSet] = useState(false);
  const { mutate: getEmployee, data: getEmployeeDetails, isLoading: loading, } = useFirstInLastOutList({
    year: yearFormat,
    month: monthFormat,
    departmentId: departmentId,
    employeeId: employeeId,
    searchKey: searchKey,
    sortField: "max(filo.employee_code)",
    sortMethod: "desc",
    pageNumber: pageNumber,
    pageSize: page,
  });
  const { mutate: downLoadCsv, isLoading: csvLoading, } = usePostDownloadCsv("filoCsv", {
    year: yearFormat,
    month: monthFormat,
    departmentId: departmentId,
    employeeId: employeeId,
    searchKey: searchKey,
    sortField: "max(filo.employee_code)",
    sortMethod: "desc",
  });

  const onFetchEmployee = (values) => {
    const formData = JSON.stringify(values);
    getEmployee(formData, {
      onSuccess: (data) => {
        setEmployeeList(data?.data?.content);
      },
    });
  };

  const onChange = (page) => {
    initialValue.pageNumber = page - 1;
    setInitialValue(initialValue);
    setPageNumber(page - 1);
  };

  const onShowSizeChange = (current, pageSize) => {
    setPage(initialValue.pageSize = pageSize)
  }

  const searchHandler = (e) => {
    initialValue.searchKey = e.target.value;
    let data = { ...initialValue };
    data.searchKey = e.target.value;
    data.pageNumber = e.target.value !== "" ? 0 : initialValue.pageNumber;
    setInitialValue(data);
    setSearchKey(e.target.value);
  };

  const refetchDownload = (values) => {
    const formData = JSON.stringify(values);
    downLoadCsv(formData, {
      onSuccess: (data) => {
        if (data) {
          exportCsvToXlsx(data, "FirstInLastOut_Report", "First In Last Out");
        }
      },
    });
  };

  // The month picker cannot be cleared (Clear Filter resets it instead), so a
  // value always arrives here.
  const dateMonth = (val) => {
    if (!val) return;
    setYearFormat(changeYearFormat(val?._d));
    setMonthFormat(changeMonthFormat(val?._d));
    initialValue.month = changeMonthFormat(val?._d);
    initialValue.year = changeYearFormat(val?._d);
    getDatesInMonth(initialValue.year, initialValue.month);
  }

  const getDatesInMonth = (year, month) => {
    if (year === null || month === null || year === undefined || month === undefined) {
      const currentDate = new Date();
      year = currentDate.getFullYear();
      // Two digits: the API matches entry_date with LIKE 'YYYY-MM%', so an
      // unpadded 9 would match nothing.
      month = String(currentDate.getMonth() + 1).padStart(2, '0');
      initialValue.year = year;
      initialValue.month = month;
    }
    var startDate = new Date(year, month - 1, 1);
    var endDate = new Date(year, month, 0);
    var datesArray = [];

    for (var i = startDate.getDate(); i <= endDate.getDate(); i++) {
      datesArray.push(`${String(i).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`);
    }
    setDates(datesArray);
    initialValue.pageNumber = 0;
    setPageNumber(0);
  }

  const checkDisabledMonth = (current) => current && current > moment().endOf("month");

  const checkDisabledCSV = () => {
    return !yearFormat ||
      yearFormat === undefined ||
      yearFormat.length === 0 ||
      !monthFormat ||
      monthFormat === undefined ||
      monthFormat.length === 0
  }

  const handleEmployeeSelect = (value) => {
    const id = value === ALL_OPTION ? null : value;
    initialValue.employeeId = id;
    initialValue.pageNumber = 0;
    setPageNumber(0);
    setEmployeeId(id);
  };

  const handleDepartmentSelect = (value) => {
    const id = value === ALL_OPTION ? null : value;
    initialValue.departmentId = id;
    initialValue.pageNumber = 0;
    setPageNumber(0);
    setDepartmentId(id);
  };

  // const handleSiteSelect = (value) => {
  //   initialValue.siteId = value;
  //   initialValue.pageNumber = 0;
  //   setPageNumber(0);
  //   setSiteId(value);
  // };

  const clearDepartment = () => {
    setDepartmentId(null);
    initialValue.departmentId = null;
  }

  const clearEmployee = () => {
    setEmployeeId(null);
    initialValue.employeeId = null;
  }

  // const clearsite = () => {
  //   setSiteId(null);
  //   initialValue.siteId = null;
  // }

  /** Back to the default view: current month, every contractor and employee, no search. */
  const clearFilters = () => {
    const today = moment();
    const month = today.format("MM");
    const year = today.format("YYYY");
    Object.assign(initialValue, { month, year, departmentId: null, employeeId: null, searchKey: "", pageNumber: 0 });
    setMonthFormat(month);
    setYearFormat(year);
    setDepartmentId(null);
    setEmployeeId(null);
    setSearchKey("");
    getDatesInMonth(year, month);
  };

  const isFiltered = departmentId !== null
    || employeeId !== null
    || searchKey !== ""
    || (monthFormat !== null && monthFormat !== moment().format("MM"))
    || (yearFormat !== null && yearFormat !== moment().format("YYYY"));

  // The filter controls are controlled so Clear Filter can reset what they show.
  const selectedMonth = yearFormat && monthFormat ? moment(`${yearFormat}-${monthFormat}`, "YYYY-MM") : moment();

  useEffect(() => {
    getDatesInMonth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!initialValuesSet) {
      let tdyDate = moment();
      setMonthFormat(tdyDate.format("MM"));
      setYearFormat(tdyDate.format("YYYY"));
      initialValue.month = tdyDate.format("MM");
      initialValue.year = tdyDate.format("YYYY");
      setInitialValuesSet(true);
    } else {
      onFetchEmployee(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, searchKey, monthFormat, yearFormat, page, departmentId, employeeId, siteId]);

  const hasRows = !loading && employeeList?.length > 0;

  return (
    <Fragment>
      <Row className="common-heads" gutter={[12, 0]}>
        <Col md={9} className="title">
          <h1>First In Last Out</h1>
          <p>Manage all FILO</p>
        </Col>
        <Col md={3}>
          <DatePicker picker="month" value={selectedMonth} allowClear={false} format={"YYYY/MM"} onChange={dateMonth} placeholder={"Select year and month"} disabledDate={checkDisabledMonth} />
        </Col>
        <Col md={3}>
          <Select value={departmentId ?? ALL_OPTION} onClear={clearDepartment} placeholder="Select contractor" onSelect={handleDepartmentSelect}>
            <Option value={ALL_OPTION}>All Contractors</Option>
            {departmentData?.data?.map((common, a) => (
              <Option key={a} value={common?.departmentId}>
                {common?.departmentName}
              </Option>
            ))}
          </Select>
        </Col>
        <Col md={3}>
          <Select value={employeeId ?? ALL_OPTION} onClear={clearEmployee} placeholder="Select Employee" showSearch onSelect={handleEmployeeSelect}
            filterOption={(input, option) => option?.children?.includes(input)}
            filterSort={(optionA, optionB) => optionA?.children?.toLowerCase()?.localeCompare(optionB?.children?.toLowerCase())}>
            <Option value={ALL_OPTION}>All Employees</Option>
            {employeeData?.data?.map((common, a) => (
              <Option key={a} value={common?.employeeId}>{common?.employeeName}</Option>
            ))}
          </Select>
        </Col>
        {/* <Col md={3}>
         <Select allowClear onClear={clearsite} placeholder={"Select Site"} showSearch onSelect={handleSiteSelect}>
                            {siteDropdownData?.data.map((item, a) => (
                                <Option value={item.siteId} key={a}>{item?.siteName}</Option>
                            ))}
                        </Select>
        </Col> */}
        <Col md={3}>
          <Input className="report-search" value={searchKey} onChange={searchHandler} placeholder="Search" prefix={<Image className="search-img" name={"search.svg"} />} allowClear />
        </Col>
        <Col className="filo-clear">
          <Button className="filo-clear-btn" disabled={!isFiltered} onClick={clearFilters}>Clear Filter</Button>
        </Col>
        <Col md={3}>
          <Button disabled={checkDisabledCSV()} type="primary" onClick={() => refetchDownload(initialValue)} className="csv" loading={csvLoading}>Download Excel</Button>
        </Col>
      </Row>

      <div className="common-table filo-table">
        <table>
          <thead>
            <tr>
              <th className="filo-fixed filo-code"><div className="filo-cell">Card ID</div></th>
              <th className="filo-fixed filo-name"><div className="filo-cell">Employee Name</div></th>
              <th className="filo-fixed filo-contractor"><div className="filo-cell">Contractor</div></th>
              {dates?.map((date) => (
                <th key={date}>{date}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!hasRows ? (
              <TableStateRow colSpan={FIXED_COLUMN_COUNT + (dates?.length || 0)}>
                <span className="filo-state">
                  {loading ? <span className="table-state-spinner" role="status" aria-label="Loading" /> : "No employees found"}
                </span>
              </TableStateRow>
            ) : (
              employeeList.map((item, index) => (
                <tr key={item?.employeeId ?? index}>
                  <td className="filo-fixed filo-code"><div className="filo-cell" title={item?.employeeCode}>{item?.employeeCode}</div></td>
                  <td className="filo-fixed filo-name"><div className="filo-cell" title={item?.employeeName}>{item?.employeeName}</div></td>
                  <td className="filo-fixed filo-contractor"><div className="filo-cell" title={item?.department}>{item?.department}</div></td>
                  {dates?.map((date) => {
                    const res = item?.resDtoList?.find((res) => moment(res.date).format("DD") === date.split("-")[0]);
                    return <td className={(res?.holiday === true) ? (res?.lateIn || res?.earlyOut) ? "holiday-late-entry-cell" : "holiday-cell" : (res?.lateIn || res?.earlyOut) ? "shift-out-cell" : "default-cell"} key={date}>{timeFormatOnly(res?.firstIn)} {res?.firstIn ? "/" : ""} <br /> {timeFormatOnly(res?.lastOut)}</td>;
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Row className="table-pagination">
        <Col md={12}>
          <h5>Showing {(pageNumber) * page + 1} to {isNaN(getEmployeeDetails?.data?.content?.length + (pageNumber) * page) ? "" : getEmployeeDetails?.data?.content?.length + (pageNumber) * page} of {getEmployeeDetails?.data?.totalElements} entries</h5>
        </Col>
        <Col md={12} className="txt-right">
          {getEmployeeDetails?.data?.content?.length === 0 ? null : <Pagination pageSize={page} showSizeChanger pageSizeOptions={PAGE_SIZE_OPTIONS} onChange={onChange} current={pageNumber + 1} onShowSizeChange={onShowSizeChange} total={getEmployeeDetails?.data?.totalElements} />}
        </Col>
      </Row>
    </Fragment >
  );
};
export default FirstInLastOut;
